import { resolve, singleton } from 'aurelia';
import { HttpClient } from './http-client';

// One aggregated funnel step from GET /api/admin/analytics. `sessions` approximates people
// (distinct browser sessions) while `count` includes repeat actions by the same visitor.
export interface AnalyticsFunnelRow {
  eventName: string;
  count: number;
  sessions: number;
}

// The whole analytics report: the headline funnel numbers plus every recorded event.
export interface AnalyticsReport {
  days: number;
  visitSessions: number;
  signInStarts: number;
  signUps: number;
  signUpRate: number;
  rows: AnalyticsFunnelRow[];
}

// Owns the admin analytics panel: loads the signup funnel over a selectable trailing window so
// an admin can see how many visitors arrive, how many are prompted, how many start the Auth0
// redirect, and how many actually finish registering. Mirrors the other singleton admin
// services — shared state lives here and the panel binds to it directly.
@singleton()
export class AnalyticsAdminService {
  private readonly http: HttpClient = resolve(HttpClient);

  panelOpen: boolean = false;
  loading: boolean = false;
  error: string | null = null;
  days: number = 30;
  report: AnalyticsReport | null = null;

  // Human-readable labels for the raw event names, so the report reads as a funnel rather than
  // as database identifiers. Unknown names fall back to the raw value.
  private static readonly labels: Record<string, string> = {
    visit: 'Visited the site',
    welcome_shown: 'Saw the welcome overlay',
    welcome_dismissed: 'Dismissed the welcome overlay',
    nudge_shown: 'Saw a sign-up nudge',
    nudge_dismissed: 'Dismissed a sign-up nudge',
    signin_started: 'Started sign-in',
    signin_completed: 'Signed in (returning)',
    signup_completed: 'Registered (new account)'
  };

  label(eventName: string): string {
    return AnalyticsAdminService.labels[eventName] ?? eventName;
  }

  // Loads the report for the current window. Safe to call whenever the panel is opened;
  // failures leave a readable error and no report.
  async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const response = await this.http.get(`/api/admin/analytics?days=${this.days}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      this.report = await response.json();
    } catch (e: any) {
      this.error = `Could not load the analytics report: ${e.message}`;
      this.report = null;
    } finally {
      this.loading = false;
    }
  }

  // Switches the reporting window and reloads.
  async setDays(days: number): Promise<void> {
    this.days = days;
    await this.load();
  }

  async openPanel(): Promise<void> {
    this.panelOpen = true;
    await this.load();
  }

  closePanel(): void {
    this.panelOpen = false;
    this.error = null;
  }
}
